import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import {
  useGetAllSubscriptions,
  useGetMultipleSubscriptionFeatures,
  useGetUserSubscription,
  useConfirmSubscription,
  useCancelSubscription,
} from '../api/subscriptions';
import type { SubscriptionPlan, SubscriptionFeature } from '../api/subscriptions';
import { Button } from '../components/ui/Button';
import ConfirmModal from '../components/ui/ConfirmModal';
import { Input } from '../components/ui/Input';
import crownIcon from '../assets/images/crown.png';
import checkIcon from '../assets/images/checked.png';
import { generateFlutterwaveSubscriptionLink, redirectToPaymentLink } from '../utils/flutterwave';
import { useAppContext } from '../contexts/Storage';

const formatPlanName = (name: string): string => {
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const { setNavLinks } = useNavLinksContext();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [promoCode, setPromoCode] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);

  useEffect(() => {
    setNavLinks({
      links: [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/subscription', label: 'Subscription' },
      ],
    });
  }, [setNavLinks]);

  const {
    data: plans,
    isLoading: isLoadingPlans,
  } = useGetAllSubscriptions({
    filters: { subscription_type: billingPeriod },
  });

  console.log('plans', plans);

  const { data: userSubData } = useGetUserSubscription();
  const currentSubscriptionName = userSubData?.activeSubscription?.subscription_name ?? null;

  const planIds = useMemo(() => (plans ?? []).map((p) => p.id), [plans]);

  const featuresQueries = useGetMultipleSubscriptionFeatures(planIds, planIds.length > 0);

  const featuresMap = useMemo(() => {
    const map: Record<number, SubscriptionFeature[]> = {};
    featuresQueries.forEach((q) => {
      if (q.data) {
        map[q.data.subscriptionId] = q.data.features;
      }
    });
    return map;
  }, [featuresQueries]);

  const isLoadingFeatures = featuresQueries.some((q) => q.isLoading);

  const getPopularPlan = (allPlans: SubscriptionPlan[]): number | null => {
    const pro = allPlans.find((p) => p.subscription_name === 'pro');
    return pro?.id ?? null;
  };

  const popularPlanId = plans ? getPopularPlan(plans) : null;

  const { token, user } = useAppContext();
  const freeSubscribeMutation = useConfirmSubscription();
  const [subscribingPlanId, setSubscribingPlanId] = useState<number | null>(null);

  const cancelSubscriptionMutation = useCancelSubscription();
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTargetSubscriptionId, setCancelTargetSubscriptionId] = useState<number | null>(null);

  const handleCancelClick = (subscriptionId: number) => {
    setCancelTargetSubscriptionId(subscriptionId);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTargetSubscriptionId) return;
    setCancelling(true);
    try {
      await cancelSubscriptionMutation.mutateAsync({
        subscription_id: cancelTargetSubscriptionId,
      });
      toast.success('Your subscription has been cancelled successfully.');
      setShowCancelModal(false);
      setCancelTargetSubscriptionId(null);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    if (subscribingPlanId) return;
    setSubscribingPlanId(plan.id);

    try {
      if (plan.subscription_price === 0) {
        await freeSubscribeMutation.mutateAsync({
          plan_id: plan.id,
          payment_method: 'free',
        });
        toast.success('You have been subscribed to the free plan!');
        setSubscribingPlanId(null);
        return;
      }

      if (!token || !user?.id) {
        throw new Error('User not authenticated');
      }

      // Generate Flutterwave payment link
      const paymentResponse = await generateFlutterwaveSubscriptionLink(token, {
        user_id: user.id,
        subscription_id: plan.id,
        redirect_url: `${window.location.origin}/subscription/success?subscription_id=${plan.id}`,
      });

      if (paymentResponse.status === 'success' && paymentResponse.payment_link) {
        // Redirect to Flutterwave payment link in same tab
        redirectToPaymentLink(paymentResponse.payment_link);
      } else {
        throw new Error(paymentResponse.message || 'Failed to generate payment link');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to start checkout');
      setSubscribingPlanId(null);
    }
  };

  const handleApplyPromo = () => {
    if (!promoCode.trim()) return;
    setApplyingPromo(true);
    setTimeout(() => {
      console.log('Applying promo code:', promoCode);
      setApplyingPromo(false);
    }, 1000);
  };

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark overflow-y-scroll">
      <div className="">
        {/* Page Header */}
        <div className="p-6 pb-3 bg-light rounded-t-lg mb-2">
          <h3 className="text-xl">Subscription Plans</h3>
          <p className="text-grey_dark mt-1 text-sm">
            Choose the perfect plan for your business needs. All plans include our core features.
          </p>
        </div>

        {/* Billing Period Toggle */}
        <div className="bg-light rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h4 className="font-medium">Billing Period</h4>
              <p className="text-sm text-grey_dark mt-1">Choose how you want to be billed</p>
            </div>
            <div className="flex bg-bg_grey_light rounded-lg p-1">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-4 py-2 rounded-md transition-colors duration-200 text-sm font-medium ${billingPeriod === 'monthly'
                    ? 'bg-light shadow-sm text-dark'
                    : 'text-grey_dark hover:text-dark'
                  }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-4 py-2 rounded-md transition-colors duration-200 text-sm font-medium ${billingPeriod === 'yearly'
                    ? 'bg-light shadow-sm text-dark'
                    : 'text-grey_dark hover:text-dark'
                  }`}
              >
                Yearly <span className="text-primary text-sm ml-1">(Save 20%)</span>
              </button>
            </div>
          </div>
        </div>

        {/* Plans Grid */}
        {isLoadingPlans ? (
          <div className="text-center py-16 text-grey_dark">Loading subscription plans...</div>
        ) : !plans || plans.length === 0 ? (
          <div className="text-center py-16 text-grey_dark">
            No subscription plans available for {billingPeriod} billing.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {plans.map((plan) => {
              const isPopular = plan.id === popularPlanId;
              const isCurrentPlan = currentSubscriptionName === plan.subscription_name;
              const features = featuresMap[plan.id] ?? [];
              const loadingThisFeatures = !featuresMap[plan.id] && isLoadingFeatures;

              return (
                <div
                  key={plan.id}
                  className={`bg-light border rounded-xl p-6 relative transition-all duration-300 hover:shadow-lg ${isPopular ? 'border-primary ring-2 ring-primary' : 'border-grey_light'
                    }`}
                >
                  {/* Popular Badge */}
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-medium">
                        Most Popular
                      </span>
                    </div>
                  )}

                  {/* Plan Header */}
                  <div className="flex items-center mb-4">
                    <img src={crownIcon} alt={plan.subscription_name} className="w-10 h-10 mr-3" />
                    <div>
                      <h4 className="text-xl font-bold text-dark">
                        {formatPlanName(plan.subscription_name)}
                      </h4>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-dark">
                        ${plan.subscription_price.toFixed(2)}
                      </span>
                      <span className="text-grey_dark ml-2">
                        /{billingPeriod === 'yearly' ? 'year' : 'month'}
                      </span>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="mb-6 space-y-3 min-h-[200px]">
                    {loadingThisFeatures ? (
                      <div className="text-sm text-grey_dark">Loading features...</div>
                    ) : features.length === 0 ? (
                      <div className="text-sm text-grey_dark">No features listed</div>
                    ) : (
                      features.map((feature) => (
                        <div key={feature.id} className="flex items-start">
                          <img
                            src={checkIcon}
                            alt="Included"
                            className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0"
                          />
                          <div>
                            <span className="text-sm text-dark">{feature.feature_name}</span>
                            {feature.feature_credit_point_price > 0 && (
                              <span className="text-xs text-grey_dark ml-1">
                                ({feature.feature_credit_point_price} credits)
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Action Button */}
                  <Button
                    onClick={() => handleSubscribe(plan)}
                    variant={isPopular ? 'filled-primary' : 'bordered-primary'}
                    className={`w-full ${isCurrentPlan || subscribingPlanId === plan.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                    disabled={isCurrentPlan || subscribingPlanId !== null}
                  >
                    {isCurrentPlan
                      ? 'Current Plan'
                      : subscribingPlanId === plan.id
                        ? 'Redirecting to Checkout...'
                        : plan.subscription_price === 0
                          ? 'Get Started'
                          : 'Subscribe Now'}
                  </Button>

                  {/* Current Plan Indicator & Cancel Button */}
                  {isCurrentPlan && (
                    <div className="mt-3 space-y-2">
                      <div className="text-center text-sm text-primary font-medium">
                        ✓ You are currently on this plan
                      </div>
                      {plan.subscription_price > 0 && userSubData?.activeSubscription?.subscription_id && (
                        <Button
                          onClick={() => handleCancelClick(userSubData.activeSubscription!.subscription_id)}
                          variant="bordered-danger"
                          className="w-full text-sm"
                          disabled={cancelling}
                        >
                          {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Promo Code Section */}
        <div className="bg-light rounded-lg p-6 mb-8">
          <h4 className="text-lg font-medium mb-4">Have a promo code?</h4>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="text"
              attributes={{
                placeholder: 'Enter promo code',
                value: promoCode,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => setPromoCode(e.target.value),
              }}
              className="flex-1"
            />
            <Button
              onClick={handleApplyPromo}
              variant="bordered-primary"
              disabled={applyingPromo || !promoCode.trim()}
              className="whitespace-nowrap"
            >
              {applyingPromo ? 'Applying...' : 'Apply Code'}
            </Button>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-light rounded-lg p-6">
          <h4 className="text-lg font-medium mb-6">Frequently Asked Questions</h4>
          <div className="space-y-4">
            <div className="border-b border-grey_light pb-4">
              <h5 className="font-medium mb-2">Can I switch plans at any time?</h5>
              <p className="text-sm text-grey_dark">
                Yes, you can upgrade or downgrade your plan at any time. Changes will be prorated
                and reflected in your next billing cycle.
              </p>
            </div>
            <div className="border-b border-grey_light pb-4">
              <h5 className="font-medium mb-2">Is there a free plan?</h5>
              <p className="text-sm text-grey_dark">
                Yes. Our freemium plan is free forever with core features. No credit card required
                to get started — upgrade anytime when you need more.
              </p>
            </div>
            <div className="border-b border-grey_light pb-4">
              <h5 className="font-medium mb-2">What payment methods do you accept?</h5>
              <p className="text-sm text-grey_dark">
                We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and
                bank transfers for annual plans.
              </p>
            </div>
            <div>
              <h5 className="font-medium mb-2">Can I cancel my subscription?</h5>
              <p className="text-sm text-grey_dark">
                Yes, you can cancel your subscription at any time. You'll continue to have access
                until the end of your billing period.
              </p>
            </div>
          </div>
        </div>

        {/* Cancel Subscription Confirmation Modal */}
        <ConfirmModal
          isOpen={showCancelModal}
          title="Cancel Subscription"
          message="Are you sure you want to cancel your subscription? You will continue to have access until the end of your current billing period."
          confirmText={cancelling ? 'Cancelling...' : 'Yes, Cancel'}
          cancelText="Keep Subscription"
          onConfirm={handleConfirmCancel}
          onCancel={() => {
            setShowCancelModal(false);
            setCancelTargetSubscriptionId(null);
          }}
          variant="danger"
        />
      </div>
    </div>
  );
};

export default SubscriptionPage;
